import React, { useEffect, useState } from 'react';
import { styles } from './DigitalProductBlock.css';
import { Icon, IconNameEnum } from '@jbhi-fi/lanyard-ui/components/Icon';
import { PossiblyResponsiveValue, useResponsiveValue } from '../../hooks/useResponsiveValue';
import Checkbox from '@jbhi-fi/lanyard-ui/components/Checkbox';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { useIdPrefix } from '../../IdPrefix';
import { useNavigate } from '../../PlatformFunctionality/utils/useNavigate';

export type DigitalProductBlockProps = {
  heading: string;
  headingLogoName: IconNameEnum | keyof typeof IconNameEnum;
  headingLogoSize?: PossiblyResponsiveValue<{ width: number; height: number }>;
  subText?: string;
  attach: {
    heading?: string;
    subText?: string;
    features?: string;
  };
  onAdd: () => void;
  onRemove: (offeringId?: string) => void;
  hyperLinkText?: string;
  hyperLinkUrl?: string;
  alreadyInCart?: boolean;
  showMaxQtyAlert: () => void;
  imageSrc?: string;
};

const Wrapper = ({
  heading,
  headingLogoName,
  headingLogoSize,
  subText,
  testIdPrefix,
  children,
}: Pick<DigitalProductBlockProps, 'heading' | 'headingLogoName' | 'subText'> & {
  children: React.ReactNode;
  headingLogoSize: PossiblyResponsiveValue<{ width: number; height: number }>;
  testIdPrefix?: string;
}) => {
  const { height: logoHeight, width: logoWidth } = useResponsiveValue(headingLogoSize);

  return (
    <div className={styles.wrapperContainer} data-testid={`${testIdPrefix}service-block`}>
      <div className={styles.headingContainer} data-testid={`${testIdPrefix}service-heading-container`}>
        <Icon
          className={styles.headingIcon}
          height={logoHeight}
          width={logoWidth}
          name={headingLogoName}
          data-testid={`${testIdPrefix}service-icon`}
        />
        <CalloutFont className={styles.heading} as="h3" testId={`${testIdPrefix}service-heading`}>
          {heading}
        </CalloutFont>
      </div>
      {subText ? (
        <p className={styles.subText} data-testid={`${testIdPrefix}service-sub-text`}>
          {subText}
        </p>
      ) : null}
      {children}
    </div>
  );
};

export const DigitalProductBlock = ({
  heading,
  headingLogoName,
  headingLogoSize = { width: 30, height: 30 },
  subText,
  onAdd,
  onRemove,
  attach,
  hyperLinkText,
  hyperLinkUrl,
  alreadyInCart,
  showMaxQtyAlert,
  imageSrc,
}: DigitalProductBlockProps) => {
  const [clicked, setClicked] = useState(false);
  const testIdPrefix = useIdPrefix() || '';
  const navigateTo = useNavigate();

  const checkboxClickHandler = () => {
    if (!clicked) {
      alreadyInCart ? showMaxQtyAlert() : onAdd();
    }
    setClicked(!clicked);
  };

  const linkClickHandler = () => {
    navigateTo(hyperLinkUrl ?? '');
  };
  return (
    <Wrapper
      heading={heading}
      headingLogoName={headingLogoName}
      headingLogoSize={headingLogoSize}
      subText={subText}
      testIdPrefix={testIdPrefix}
    >
      <div className={styles.checkboxContainer} data-testid={`${testIdPrefix}digital-product-checkbox-container`}>
        <Checkbox
          name="add-digital-product"
          label=""
          onChange={checkboxClickHandler}
          checked={clicked}
          className={'DigitalProductBlock__checkbox ' + styles.checkbox}
          data-testid={`${testIdPrefix}digital-product-checkbox`}
          id="digital-product-checkbox"
        />
        <div className={styles.contentContainer} data-testid={`${testIdPrefix}digital-product-content-container`}>
          <button
            onClick={checkboxClickHandler}
            className={styles.contentHeading}
            data-testid={`${testIdPrefix}digital-product-heading`}
          >
            {attach.heading}
          </button>
          {attach.subText ? (
            <p className={styles.contentSubText} data-testid={`${testIdPrefix}digital-product-sub-text`}>
              {attach.subText}
            </p>
          ) : null}
          {imageSrc ? (
            <div className={styles.imageContainer}>
              <img src={imageSrc} alt="attach-logo" />
            </div>
          ) : null}
          <ul className={styles.list} data-testid={`${testIdPrefix}digital-product-list`}>
            {attach?.features &&
              attach?.features?.split('/').map((item, index) => (
                <li key={index} className={styles.listItem}>
                  {item}
                </li>
              ))}
          </ul>
          <button
            className={styles.moreInfoLink}
            onClick={linkClickHandler}
            data-testid={`${testIdPrefix}digital-product-link`}
          >
            {hyperLinkText}
          </button>
        </div>
      </div>
    </Wrapper>
  );
};
